/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unnecessary-condition */
// Porta literal de contexts/responseErrorContext.tsx do legado (discovery/03 §3.7 e risco 18):
// portar como está, tipos frouxos como lá, coberto por testes de caracterização. Não "melhore"
// sem teste que prove a mudança; a mensagem que o operador vê depende de cada ramo daqui.
import { t } from '@lingui/core/macro';

/** Erro de campo vindo de `INVALID_FORM`: `key` em minúsculas, casado por `includes` no nome do campo. */
export interface FieldError {
  key: string;
  helpMessage: string;
}

/** O que o legado passava para `mapErrorResponse`: o corpo da resposta ou um stub com `code`. */
export interface LegacyErrorPayload {
  type?: 'error' | undefined;
  code?: string | undefined;
  message?: string | undefined;
  correlationId?: string | undefined;
  details?: any;
  errors?: any[] | undefined;
  response?: { data?: any } | undefined;
  [key: string]: unknown;
}

export interface ResponseMessage {
  code?: string | undefined;
  errorMessage: string;
  correlationId?: string | undefined;
  message?: string | undefined;
  fieldErrors?: FieldError[] | undefined;
  warningToastError?: string | undefined;
}

const invalidCodes = ['ERR_NETWORK'];

// Chaves do backend (não é texto de UI).
/* eslint-disable lingui/no-unlocalized-strings */
const listRegex = [
  '',
  'Uploads',
  'Owner.Address',
  'BankAccounts',
  'amortization.amortizationType',
  'RelatedPerson',
  'RelatedPersonFund',
  'RelatedPersonCreditNote',
  'commissionRange',
  'CommercialAddress',
  'MaximumAbsAmortizationPeriods',
  'MaximumAbsInterestPeriods',
];
/* eslint-enable lingui/no-unlocalized-strings */

// Texto que o BACKEND manda; é casado, não exibido, por isso fica fora do Lingui.
// eslint-disable-next-line lingui/no-unlocalized-strings
const BACKEND_REQUIRED_PREFIX = 'É obrigatório informar';

export const genericMessage = () => t`Houve um erro inesperado: Por favor tente novamente mais tarde.`;

const isEmpty = (value: any) =>
  value == null ||
  (typeof value === 'string' && value.length === 0) ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0);

const isValidString = (value: any) =>
  !(isEmpty(value) || value === 'null' || value === 'undefined' || typeof value === 'number');

// eslint-disable-next-line lingui/no-unlocalized-strings -- zero em BRL, igual ao legado
const ZERO_BRL = 'R$ 0,00';

/** `formatCurrencyInCents` do legado: tira não-dígitos, divide por 100, BRL; mantém o sinal. */
export function formatCurrencyInCents(value: any): string {
  // eslint-disable-next-line lingui/no-unlocalized-strings -- símbolo de moeda, não texto
  if (typeof value === 'string' && value.includes('R$')) return value;
  const currency = Number(String(value).replace(/\D/g, ''));
  if (isNaN(currency) || value === undefined) return ZERO_BRL;
  const divided = currency / 100;
  const masked = !divided
    ? ZERO_BRL
    : divided.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return String(value).startsWith('-') ? `-${masked}` : masked;
}

function removeDuplicateData(dataArray: any[], key: string): any[] {
  const unique: any[] = [];
  const seen = new Set<unknown>();
  for (const data of dataArray) {
    const keyValue = data[key];
    if (!seen.has(keyValue)) {
      unique.push(data);
      seen.add(keyValue);
    }
  }
  return unique;
}

function isMessageInvalid(msg: string | undefined | null): boolean {
  const message = (msg ?? '').toLowerCase();
  return message === '' || message.includes('object object');
}

const getDetailsList = (resultErrorDetails: any) => {
  const details = resultErrorDetails?.details ?? resultErrorDetails;
  return details?.details?.length > 0 ? details.details : details;
};

function mapMessageDetails(formErrors: any[], defaultMessage: string, code?: string) {
  const messages: any[] = formErrors?.map((f) => f?.message ?? f?.reason ?? f?.details ?? f?.errorMessage) ?? [];
  const required = messages.filter((m) => m?.includes(BACKEND_REQUIRED_PREFIX));
  const requiredText = required.length ? required.map((r) => r.slice(22)).join(', ') : '';
  const head = messages.slice(0, 3).join(',\n') + (messages.length > 3 ? ' [...]' : '');
  const body = requiredText || head || defaultMessage;
  const prefix = code === 'INVALID_FORM' && requiredText ? `${t`É obrigatório informar`}: ` : `${defaultMessage}: `;
  return `${prefix} ${body}`;
}

function mapDetails(details: any, code?: string): string {
  const list = getDetailsList(details) ?? details;
  if (!list || list.length === 0) return genericMessage();
  const joined = list.map((d: any) => d?.errorMessage || d?.message).join(', ');
  return mapMessageDetails(list, genericMessage(), code) || joined;
}

function checkForCustomErrorMessages(formErrors: { key: string }[]): boolean {
  if (!formErrors || formErrors.length === 0) return false;
  return formErrors.every(({ key }) => {
    const i = key?.indexOf('[');
    const base = key?.slice(0, i !== -1 ? i : key.length);
    const top = base?.slice(0, base.indexOf('.'));
    if (top === 'CommercialAddress') return true;
    return isMessageInvalid(base) || listRegex.includes(base);
  });
}

function mapFormErrors(details: any, defaultMessage: string, code?: string) {
  const { formErrors, ...rest } = details ?? {};
  const otherErrors = rest;
  if (Object.values(otherErrors || {}).length > 0) {
    return { fieldErrors: undefined, message: otherErrors[0]?.errorMessage ?? defaultMessage ?? genericMessage() };
  }
  if (!formErrors || formErrors.length === 0) return { fieldErrors: undefined, message: undefined };

  const isCustom = checkForCustomErrorMessages(formErrors);
  const emptyKey = formErrors.find((f: any) => (f.key ?? '').length === 0);
  if (emptyKey) return { fieldErrors: undefined, message: emptyKey.message ?? defaultMessage };

  const unique = removeDuplicateData(formErrors, 'message');
  const messages = unique.map(({ message }) => message);
  const fieldErrors: FieldError[] = unique.map(({ key, message }) => ({ key: key?.toLowerCase(), helpMessage: message }));
  const errorMessage = `${defaultMessage}: ${messages.join(', ')}`;
  // Os dois ramos do legado são iguais; mantido assim de propósito (paridade).
  const warningToastError = errorMessage;

  return { message: errorMessage, fieldErrors: isCustom ? undefined : fieldErrors, warningToastError, code };
}

/** Paridade com `mapErrorResponse` do legado: mensagem, erros de campo e texto do toast de aviso. */
export function mapErrorResponse(responseError: LegacyErrorPayload): ResponseMessage {
  const hasTopLevelCode = responseError?.code !== undefined;
  const hasDataMessage = !!responseError?.response?.data?.message;
  if ((!hasTopLevelCode || invalidCodes.includes(responseError?.code as string)) && !hasDataMessage) {
    return { correlationId: '', errorMessage: responseError?.message ?? genericMessage(), code: '' };
  }
  const codeByResponse = responseError?.code;
  const codeByDataResponse = responseError?.response?.data?.code;
  if (codeByResponse === 'INVALID_FORM' || codeByDataResponse === 'INVALID_FORM') return mapFormError(responseError);
  return mapGenericError(responseError);
}

// Devolve `message` cru (pode ser undefined), como o legado; o template do chamador vira 'undefined'.
function formatErrorBillingDataIntegration({ details, message }: { details: any; message: any }): any {
  return isMessageInvalid(details) ? message : `${message}: ${details}`;
}

function invalidBusinessRuleMessage(details: any, defaultMessage?: string): string {
  const formErrors = details?.formErrors ?? [];
  if (!isEmpty(formErrors)) return formErrors.map((f: any) => f?.message).join('. ');
  if (details?.length > 0 && typeof details === 'object') {
    const hasNames = details.every((x: any) => x?.memberNames?.length > 0);
    const joined = details.map((x: any) => x?.errorMessage).join(', ');
    return hasNames && joined.length > 0 ? joined : genericMessage();
  }
  return defaultMessage ?? genericMessage();
}

function getMessageLimit(details: any) {
  const when = details?.isBusinessHour ? t`em horário comercial` : t`fora do horário comercial`;
  const transactionType = details?.transactionTypeDisplay;
  const value = formatCurrencyInCents(details.transferValue);
  const personType = details.personTypeDisplay;
  const limit = formatCurrencyInCents(details.currentLimit);
  return t`Sua transação ${transactionType} no valor de ${value} para ${personType} ${when} não pôde ser processada. Limite atual: ${limit}. Solicite aumento do seu limite na aba 'Meus Limites'`;
}

function mapGenericError(responseError: LegacyErrorPayload): ResponseMessage {
  const resultError: any = responseError?.response?.data ?? responseError;
  if (resultError?.details === null) {
    return { correlationId: resultError.correlationId, errorMessage: resultError.message, code: responseError?.code };
  }

  const formatDetail = (details: any, key: string, label: string, isCurrency = false) => {
    if (!details?.[key]) return '';
    const v = details[key];
    return isCurrency ? `${label}: ${formatCurrencyInCents(v)}` : `${label}: ${v}`;
  };

  const mapAmortizationErrors = (details: any) => {
    if (!details) return '';
    const mappings = [
      { key: 'principalAmountInCents', label: t`Valor do contrato`, isCurrency: true },
      { key: 'valueNumberBankNotes', label: t`Valor da nota`, isCurrency: true },
      { key: 'productMaximumPrincipalAmount', label: t`Valor máximo do produto`, isCurrency: true },
      { key: 'productMinimumPrincipalAmount', label: t`Valor mínimo do produto`, isCurrency: true },
      { key: 'liquidValue', label: t`Valor líquido`, isCurrency: true },
      { key: 'comissions', label: t`Emissões`, isCurrency: true },
      { key: 'financeTax', label: t`IOF`, isCurrency: true },
      { key: 'firstDueDate', label: t`Data da primeira parcela` },
      { key: 'startDate', label: t`Data de início` },
      { key: 'expectedFinanceTax', label: t`IOF esperado`, isCurrency: true },
      { key: 'receivedFinanceTax', label: t`IOF enviado`, isCurrency: true },
      { key: 'lastPrincipalAmountInCents', label: t`Saldo devedor final`, isCurrency: true },
    ];
    return mappings
      .map(({ key, label, isCurrency }) => formatDetail(details, key, label, isCurrency))
      .filter(Boolean)
      .join(', ');
  };

  const mapformErrors = (formErrors: any) =>
    formErrors == null || formErrors.length === 0
      ? resultError?.message
      : mapMessageDetails(formErrors, resultError?.message, resultError?.code);

  const details = resultError?.details;
  const mapErrorDetails = (error: any) => {
    const d = error?.details?.details;
    if (isEmpty(d)) return genericMessage();
    if (Array.isArray(d)) return `${error?.message} ${d.map((it) => it?.errorMessage).join(' ')}`;
    return genericMessage();
  };

  const mapLiquidationScheduleError = (error: any) => {
    if (Object.values(error?.details ?? {}).length > 0) return ` ${error.message}: ${error?.details?.message}`;
    return error.message ?? genericMessage();
  };

  const errors: Record<string, () => string> = {
    INVALID_MFA: () => `${details?.reason ?? resultError?.message}`,
    BILLING_DATA_INTEGRATION_ERROR: () =>
      `${formatErrorBillingDataIntegration({ details: details?.details, message: resultError?.message })}`,
    DATAPREV_DATA_INTEGRATION_ERROR: () => mapErrorDetails(resultError),
    FGTS_DATA_INTEGRATION_ERROR: () =>
      `${resultError?.message}${!isValidString(resultError?.details?.reason) ? '' : `: ${details?.reason ?? genericMessage()}`}`,
    RESOURCE_ID_NOT_FOUND: () =>
      `${resultError?.message} ${isMessageInvalid(resultError.details?.errorMessages) ? `: ${details?.errorMessages}` : ''}`,
    UNEXPECTED_EXCEPTION: () => `${resultError?.message}`,
    INVALID_AMORTIZATION_QUERY: () => `${resultError?.message} ${mapAmortizationErrors(details)}`,
    INVALID_FORM: () =>
      details?.length > 0 ? mapDetails(details, resultError?.code) : (mapformErrors(details?.formErrors) ?? resultError?.message),
    INSUFICIENT_TRANSACTION_LIMIT: () => (details ? getMessageLimit(details) : resultError?.message),
    RECORD_NOT_EXISTS_OR_MISSING_PERMISSION: () => `${resultError?.message} ${details?.resource ? `(${details?.resource})` : ''}`,
    INVALID_ACTION_FOR_RECORD: () => `${resultError?.message ?? genericMessage()} ${details?.details ?? ''}`,
    INVALID_BUSINESS_RULE: () => invalidBusinessRuleMessage(details, resultError?.message),
    SEMEAR_INSS_DATA_INTEGRATION_ERROR: () => mapErrorDetails(resultError),
    SIAPE_DATA_INTEGRATION_ERROR: () => mapErrorDetails(resultError),
    ZETRA_DATA_INTEGRATION_ERROR: () => mapErrorDetails(resultError),
    ZIP_CODE_INVALID: () =>
      details?.formErrors && details.formErrors.length > 0
        ? details.formErrors.map((f: any) => f?.message).join('. ')
        : resultError?.message,
    DATAPREV_MAINTENANCE: () =>
      details?.formErrors && details.formErrors.length > 0
        ? details.formErrors.map((f: any) => f?.message).join('. ')
        : resultError?.message,
    LIQUIDATION_SCHEDULE_INVALID: () => mapLiquidationScheduleError(resultError),
  };

  const code: string | undefined = resultError?.code;
  const handler = code !== undefined && Object.hasOwn(errors, code) ? errors[code] : undefined;
  const errorOnlyString = typeof resultError?.details === 'string' ? `${resultError?.message}: ${resultError?.details}` : resultError?.message;
  let computedMessage: string | undefined = handler ? handler() : errorOnlyString;
  if (!computedMessage || isMessageInvalid(computedMessage) || computedMessage === genericMessage()) {
    computedMessage = resultError?.message ?? errorOnlyString;
  }

  const reasonErr =
    resultError?.details?.code === 'FGTS_DATA_INTEGRATION_ERROR' ? resultError?.details?.reason : (computedMessage ?? resultError?.message);
  return {
    code: responseError?.response?.data?.code ?? responseError.code,
    errorMessage: computedMessage ?? genericMessage(),
    correlationId: resultError?.correlationId ?? '',
    message: resultError?.message ?? genericMessage(),
    warningToastError: reasonErr,
  };
}

function mapFormError(responseError: LegacyErrorPayload): ResponseMessage {
  const details = responseError?.details ?? responseError?.response?.data?.details;
  const errorsByBadRequest = responseError?.response?.data?.details;
  const messageResult = responseError?.response?.data?.message ?? responseError?.message ?? genericMessage();
  const code = responseError?.code;
  const codeByDataResponse = responseError?.response?.data?.code;
  const invalidForm = code === 'INVALID_FORM' ? code : codeByDataResponse;

  if (errorsByBadRequest !== undefined && code === 'ERR_BAD_REQUEST' && !invalidForm) {
    const message =
      typeof errorsByBadRequest === 'object' && errorsByBadRequest?.length > 0 ? mapDetails(errorsByBadRequest, code) : messageResult;
    return { code, correlationId: responseError?.correlationId, errorMessage: message ?? genericMessage() };
  }

  const { fieldErrors, warningToastError, message } = mapFormErrors(details, messageResult, invalidForm);
  return {
    code,
    correlationId: responseError?.correlationId,
    errorMessage: message ?? messageResult ?? genericMessage(),
    fieldErrors,
    warningToastError,
    message,
  };
}
